<script setup lang="ts">
import { Background } from '@vue-flow/background'
import { type Edge, type Node, type NodeMouseEvent, VueFlow } from '@vue-flow/core'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'

/**
 * The one Vue Flow surface this portal draws on. Every canvas here is a read-only picture of something the
 * database already decided — a task workflow, the org's circles — so nothing is dragged, connected or
 * selected, and the caller keeps only what differs: the elements, how far it may zoom, and what a click means.
 *
 * The Vue Flow store belongs to the caller (`useVueFlow(id)` there, `:id` here), so two canvases on one page
 * zoom and fit independently.
 */
const props = withDefaults(
    defineProps<{
        id: string
        nodes: Node[]
        edges: Edge[]
        minZoom?: number
        maxZoom?: number
        panOnScroll?: boolean
        zoomOnScroll?: boolean
        ariaLabel?: string
    }>(),
    {
        minZoom: 0.08,
        maxZoom: 1.75,
        panOnScroll: true,
        zoomOnScroll: false,
        ariaLabel: undefined,
    },
)

const emit = defineEmits<{
    'node-click': [NodeMouseEvent]
    'node-mouse-enter': [NodeMouseEvent]
    'node-mouse-leave': [NodeMouseEvent]
}>()
</script>

<template>
	<VueFlow
		:id="props.id"
		:nodes="props.nodes"
		:edges="props.edges"
		:aria-label="props.ariaLabel"
		:nodes-draggable="false"
		:nodes-connectable="false"
		:nodes-focusable="false"
		:edges-updatable="false"
		:edges-focusable="false"
		:elements-selectable="false"
		:zoom-on-double-click="false"
		:pan-on-scroll="props.panOnScroll"
		:zoom-on-scroll="props.zoomOnScroll"
		:zoom-on-pinch="true"
		:min-zoom="props.minZoom"
		:max-zoom="props.maxZoom"
		@node-click="emit('node-click', $event)"
		@node-mouse-enter="emit('node-mouse-enter', $event)"
		@node-mouse-leave="emit('node-mouse-leave', $event)"
	>
		<template #default>
			<Background :gap="20" :size="1" pattern-color="var(--muted-grey)" />
			<slot />
		</template>
		<template v-for="name in Object.keys($slots).filter(slot => slot !== 'default')" #[name]="slotProps">
			<slot :name="name" v-bind="slotProps ?? {}" />
		</template>
	</VueFlow>
</template>
